import { z } from 'zod'

// ─── 共通パーツ ────────────────────────────────────────────────────────────

/** オープンキャンパス（検索結果・詳細共通） */
export const MynaviOpenCampusItemSchema = z.object({
  id: z.number(),
  eventDate: z.string().nullable(),
  eventTime: z.string().nullable(),
  eventName: z.string().nullable().optional(),
  holdingInfo: z.string().nullable().optional(),
  location: z.string().nullable(),
  isWished: z.boolean(),
  detailUrl: z.string().nullable().optional(),
  point: z.number().nullable().optional(),
})
export type MynaviOpenCampusItem = z.infer<typeof MynaviOpenCampusItemSchema>

/** 学校サマリ（検索結果一覧で使用） */
export const MynaviCollegeSummarySchema = z.object({
  id: z.number(),
  name: z.string(),
  collegeType: z.string().nullable(),
  prefecture: z.string().nullable(),
  imageUrl: z.string().nullable(),
  brochureUrl: z.string().nullable(),
  openCampusUrl: z.string().nullable(),
  openCampuses: z.array(MynaviOpenCampusItemSchema),
})
export type MynaviCollegeSummary = z.infer<typeof MynaviCollegeSummarySchema>

// ─── GET /api/v2/mynavi/areas ──────────────────────────────────────────────

export const MynaviAreaItemSchema = z.object({
  name: z.string(),
  prefectures: z.array(z.string()),
})
export type MynaviAreaItem = z.infer<typeof MynaviAreaItemSchema>

export const MynaviAreasResponseSchema = z.object({
  areas: z.array(MynaviAreaItemSchema),
})
export type MynaviAreasResponse = z.infer<typeof MynaviAreasResponseSchema>

// ─── GET /api/v2/mynavi/college-types ─────────────────────────────────────

export const MynaviCollegeTypeItemSchema = z.object({
  value: z.string(),
  label: z.string(),
})
export type MynaviCollegeTypeItem = z.infer<typeof MynaviCollegeTypeItemSchema>

export const MynaviCollegeTypesResponseSchema = z.object({
  collegeTypes: z.array(MynaviCollegeTypeItemSchema),
})
export type MynaviCollegeTypesResponse = z.infer<typeof MynaviCollegeTypesResponseSchema>

// ─── GET /api/v2/mynavi/colleges（インクリメンタルサーチ）─────────────────

export const MynaviCollegeIncrementalItemSchema = z.object({
  id: z.number(),
  name: z.string(),
})
export type MynaviCollegeIncrementalItem = z.infer<typeof MynaviCollegeIncrementalItemSchema>

export const MynaviCollegesIncrementalResponseSchema = z.object({
  total: z.number(),
  colleges: z.array(MynaviCollegeIncrementalItemSchema),
})
export type MynaviCollegesIncrementalResponse = z.infer<typeof MynaviCollegesIncrementalResponseSchema>

// ─── GET /api/v2/mynavi/colleges/search ───────────────────────────────────

export const MynaviCollegesSearchResponseSchema = z.object({
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  colleges: z.array(MynaviCollegeSummarySchema),
})
export type MynaviCollegesSearchResponse = z.infer<typeof MynaviCollegesSearchResponseSchema>

// ─── GET /api/v2/mynavi/colleges/filter ───────────────────────────────────
// ※ バックエンドのOpenAPI仕様では200レスポンスのスキーマ未定義。
//    searchと同構造（total/page/limit/colleges）と推測してここで定義する。
//    不一致があれば実装時に修正すること。

export const MynaviCollegesFilterResponseSchema = z.object({
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  colleges: z.array(MynaviCollegeSummarySchema),
})
export type MynaviCollegesFilterResponse = z.infer<typeof MynaviCollegesFilterResponseSchema>

// ─── GET /api/v2/mynavi/colleges/filter/count ─────────────────────────────

export const MynaviCollegesFilterCountResponseSchema = z.object({
  count: z.number(),
})
export type MynaviCollegesFilterCountResponse = z.infer<typeof MynaviCollegesFilterCountResponseSchema>

// ─── GET /api/v2/mynavi/colleges/{id}（学校詳細）─────────────────────────

export const MynaviStudyFieldItemSchema = z.object({
  id: z.number(),
  name: z.string(),
})
export type MynaviStudyFieldItem = z.infer<typeof MynaviStudyFieldItemSchema>

export const MynaviJobCategoryItemSchema = z.object({
  id: z.number(),
  name: z.string(),
})
export type MynaviJobCategoryItem = z.infer<typeof MynaviJobCategoryItemSchema>

export const MynaviCollegeDetailResponseSchema = z.object({
  id: z.number(),
  name: z.string(),
  collegeType: z.string().nullable(),
  description: z.string().nullable(),
  prefecture: z.string().nullable(),
  imageUrl: z.string().nullable(),
  brochureUrl: z.string().nullable(),
  openCampusUrl: z.string().nullable(),
  studyFields: z.array(MynaviStudyFieldItemSchema),
  jobCategories: z.array(MynaviJobCategoryItemSchema),
  openCampuses: z.array(MynaviOpenCampusItemSchema),
})
export type MynaviCollegeDetailResponse = z.infer<typeof MynaviCollegeDetailResponseSchema>

// ─── GET /api/v2/mynavi/study-fields（フィルター用・2階層ネスト）────────────

export type MynaviStudyFieldNode = {
  id: number
  name: string
  children: MynaviStudyFieldNode[] | null
}

export const MynaviStudyFieldNodeSchema: z.ZodType<MynaviStudyFieldNode> = z.lazy(() =>
  z.object({
    id: z.number(),
    name: z.string(),
    children: z.array(MynaviStudyFieldNodeSchema).nullable(),
  }),
)

export const MynaviStudyFieldsResponseSchema = z.object({
  studyFields: z.array(MynaviStudyFieldNodeSchema),
})
export type MynaviStudyFieldsResponse = z.infer<typeof MynaviStudyFieldsResponseSchema>

// ─── GET /api/v2/mynavi/job-categories（フィルター用・3階層ネスト）────────

export type MynaviJobCategoryNode = {
  id: number
  name: string
  children: MynaviJobCategoryNode[] | null
}

export const MynaviJobCategoryNodeSchema: z.ZodType<MynaviJobCategoryNode> = z.lazy(() =>
  z.object({
    id: z.number(),
    name: z.string(),
    children: z.array(MynaviJobCategoryNodeSchema).nullable(),
  }),
)

export const MynaviJobCategoriesResponseSchema = z.object({
  jobCategories: z.array(MynaviJobCategoryNodeSchema),
})
export type MynaviJobCategoriesResponse = z.infer<typeof MynaviJobCategoriesResponseSchema>

// ─── GET /api/v2/mynavi/open-campuses（近くのOC一覧）─────────────────────

export const MynaviNearbyCollegeSchema = z.object({
  id: z.number().nullable(),
  name: z.string().nullable(),
  imageUrl: z.string().nullable(),
  brochureUrl: z.string().nullable(),
  openCampusUrl: z.string().nullable(),
})

export const MynaviNearbyCampusSchema = z.object({
  id: z.number().nullable(),
  name: z.string().nullable(),
})

export const MynaviNearbyStampSchema = z.object({
  id: z.number().nullable(),
  name: z.string().nullable(),
  imageUrl: z.string().nullable(),
  stampRange: z.number().int().nullable(),
})

export const MynaviNearbyOpenCampusItemSchema = z.object({
  id: z.number(),
  eventDate: z.string().nullable(),
  eventTime: z.string().nullable(),
  location: z.string().nullable(),
  detailUrl: z.string().nullable(),
  distance: z.number().int().nullable(),
  college: MynaviNearbyCollegeSchema,
  campus: MynaviNearbyCampusSchema,
  stamp: MynaviNearbyStampSchema,
})
export type MynaviNearbyOpenCampusItem = z.infer<typeof MynaviNearbyOpenCampusItemSchema>

export const MynaviOpenCampusesResponseSchema = z.object({
  openCampuses: z.array(MynaviNearbyOpenCampusItemSchema),
})
export type MynaviOpenCampusesResponse = z.infer<typeof MynaviOpenCampusesResponseSchema>

// ─── GET /api/v2/mynavi/email/check ───────────────────────────────────────

export const MynaviEmailCheckResponseSchema = z.object({
  email: z.string().nullable().optional(),
  status: z.enum(['unregistered', 'registered', 'verified']),
})
export type MynaviEmailCheckResponse = z.infer<typeof MynaviEmailCheckResponseSchema>

// ─── POST /api/v2/mynavi/email（メール登録） ──────────────────────────────

export const MynaviEmailRegisterResponseSchema = z.object({
  status: z.enum(['success', 'error']),
  message: z.string().nullable(),
})
export type MynaviEmailRegisterResponse = z.infer<typeof MynaviEmailRegisterResponseSchema>

// ─── POST /api/v2/mynavi/email/verify ─────────────────────────────────────

export const MynaviEmailVerifyResponseSchema = z.object({
  status: z.enum(['success', 'error']),
  message: z.string().nullable(),
})
export type MynaviEmailVerifyResponse = z.infer<typeof MynaviEmailVerifyResponseSchema>

// ─── POST /api/v2/mynavi/email/resend ─────────────────────────────────────
// APIドキュメントの resend 200 が MynaviEmailRegisterResponse を参照しているためエイリアス

export const MynaviEmailResendResponseSchema = MynaviEmailRegisterResponseSchema
export type MynaviEmailResendResponse = MynaviEmailRegisterResponse

// ─── POST /api/v2/mynavi/wish-to-visit ────────────────────────────────────

export const MynaviWishToVisitResponseSchema = z.object({
  isWished: z.boolean(),
})
export type MynaviWishToVisitResponse = z.infer<typeof MynaviWishToVisitResponseSchema>

// ─── GET /api/v2/mynavi/point-transactions ─────────────────────────────────

/** ポイント取引1件 */
export const MynaviPointTransactionItemSchema = z.object({
  id: z.number(),
  /**
   * アクションタイプ
   * - STAMP: スタンプ取得（獲得）
   * - REWARD: 特典交換（消費）
   * - ADMIN_GRANT: 管理者付与（獲得）
   * - ADMIN_REVOKE: 管理者取消（消費）
   * - POINT: ポイント加算（獲得、移行データ）
   * - USE_POINT: ポイント消費（消費、移行データ）
   */
  actionType: z.enum(['STAMP', 'REWARD', 'ADMIN_GRANT', 'ADMIN_REVOKE', 'POINT', 'USE_POINT']),
  /** 増減ポイント（加算=正値、消費=負値） */
  point: z.number(),
  /** スタンプ名（actionType=STAMP のみ） */
  stampName: z.string().nullable(),
  /** スポット名（actionType=STAMP のみ） */
  spotName: z.string().nullable(),
  /** 特典名（actionType=REWARD のみ） */
  rewardName: z.string().nullable(),
  /** 特典サムネイル画像 URL（actionType=REWARD のみ） */
  rewardImageUrl: z.string().nullable().optional(),
  /** 特典交換コード（actionType=REWARD のみ） */
  rewardCode: z.string().nullable().optional(),
  /** 取引日時（ISO 8601形式） */
  createdAt: z.string(),
})
export type MynaviPointTransactionItem = z.infer<typeof MynaviPointTransactionItemSchema>

export const MynaviPointTransactionsResponseSchema = z.object({
  /** 現在の合計ポイント残高 */
  totalPoint: z.number(),
  /** 取引履歴一覧 */
  transactions: z.array(MynaviPointTransactionItemSchema),
  /** 次ページが存在するか */
  hasMore: z.boolean(),
  /** 次ページ取得用カーソル（cursor-based pagination） */
  nextCursor: z.string().nullable(),
})
export type MynaviPointTransactionsResponse = z.infer<typeof MynaviPointTransactionsResponseSchema>

// ─── GET /api/v2/mynavi/open-campuses/count ───────────────────────────────
// ?date=YYYYMM を受け取り、その月の全日分の日別件数を返す

export const MynaviOpenCampusCountItemSchema = z.object({
  /** 開催日（YYYY-MM-DD） */
  date: z.string(),
  /** OC件数（0を含む） */
  count: z.number().int(),
})
export type MynaviOpenCampusCountItem = z.infer<typeof MynaviOpenCampusCountItemSchema>

export const MynaviOpenCampusCountResponseSchema = z.object({
  counts: z.array(MynaviOpenCampusCountItemSchema),
})
export type MynaviOpenCampusCountResponse = z.infer<typeof MynaviOpenCampusCountResponseSchema>

// ─── GET /api/v2/user/reward-transactions（認証必須）─────────────────────

const MynaviRewardTransactionPrizeTypeSchema = z.enum([
  'ITEM',
  'DLC',
  'AR_PHOTO',
  'COUPON',
  'GIFT_CODE',
  'GIFTEE',
])

const MynaviRewardTransactionStatusSchema = z.enum([
  'ACHIEVED',
  'EXCHANGED',
  'USED',
  'APPLIED',
  'UNAPPLIED',
])

export const MynaviRewardTransactionItemSchema = z.object({
  id: z.number(),
  status: MynaviRewardTransactionStatusSchema,
  createdAt: z.string(),
  reward: z.object({
    id: z.number(),
    name: z.string(),
    imageUrl: z.string().nullable(),
  }),
  prize: z
    .object({
      id: z.number(),
      name: z.string().nullable(),
      type: MynaviRewardTransactionPrizeTypeSchema.nullable(),
      amount: z.number().nullable(),
      giftCode: z.string().nullable(),
      giftCodeUrl: z.string().nullable(),
      giftCodeContactNumber: z.string().nullable(),
    })
    .nullable(),
})
export type MynaviRewardTransactionItem = z.infer<typeof MynaviRewardTransactionItemSchema>

export const MynaviRewardTransactionHistoryResponseSchema = z.object({
  transactions: z.array(MynaviRewardTransactionItemSchema),
  hasMore: z.boolean(),
  nextCursor: z.string().nullable(),
})
export type MynaviRewardTransactionHistoryResponse = z.infer<
  typeof MynaviRewardTransactionHistoryResponseSchema
>

